"use client";
import React, { useRef, useState, useEffect, Children } from 'react';
export default function StickyManager({ children, stickyIndices = [], stackedIndices = [], blurIndices = [], overlayIndices = [], transparentNextIndices = [] }) {
const [offsets, setOffsets] = useState({});
const refs = useRef({});
useEffect(() => {
if (stickyIndices.length === 0) return;
const updateOffsets = () => {
let currentOffset = 0;
const newOffsets = {};
const winH = typeof window !== 'undefined' ? window.innerHeight : 1000;
stickyIndices.forEach(index => {
const el = refs.current[index];
if (el) {
const isStacked = stackedIndices.includes(index);
if (isStacked) {
const height = el.offsetHeight;
newOffsets[index] = Math.min(currentOffset, winH - height);
} else {
newOffsets[index] = currentOffset;
}
if (!isStacked && !overlayIndices.includes(index)) {
currentOffset += el.offsetHeight;
}
}
});
setOffsets(prev => {
const isSame = Object.keys(newOffsets).every(k => newOffsets[k] === prev[k]);
return isSame ? prev : newOffsets;
});
};
const resizeObserver = new ResizeObserver(() => {
updateOffsets();
overlayIndices.forEach(index => {
const el = refs.current[index];
if (el) {
el.style.marginBottom = `-${el.offsetHeight}px`;
}
});
});
stickyIndices.forEach(index => {
const el = refs.current[index];
if (el) resizeObserver.observe(el);
});
window.addEventListener('resize', updateOffsets);
updateOffsets();
return () => {
resizeObserver.disconnect();
window.removeEventListener('resize', updateOffsets);
};
}, [stickyIndices, stackedIndices, overlayIndices]);
useEffect(() => {
if (stackedIndices.length === 0) return;
const elementCache = {};
const handleScroll = () => {
stackedIndices.forEach(index => {
const container = refs.current[index];
if (!container) return;
if (!elementCache[index]) {
elementCache[index] = {
overlay: container.querySelector('.stacked-white-overlay'),
content: container.querySelector('.stacked-content-blur'),
next: container.nextElementSibling,
lastProgress: -1
};
}
const cache = elementCache[index];
const { overlay, content, next: nextElement } = cache;
const isBlurred = blurIndices.includes(index);
if (!nextElement) return;
if (!isBlurred) {
if (cache.lastProgress === 0) return;
if (overlay && overlay.style.opacity !== '0') overlay.style.opacity = '0';
if (content && content.style.filter !== 'none') content.style.filter = 'none';
if (nextElement && nextElement.style) {
nextElement.style.maskImage = '';
nextElement.style.webkitMaskImage = '';
}
cache.lastProgress = 0;
return;
}
const rect = nextElement.getBoundingClientRect();
const winH = window.innerHeight;
const startPoint = winH;
const endPoint = winH * 0.5;
const current = rect.top;
let progress = 0;
if (current < startPoint) {
progress = (startPoint - current) / (startPoint - endPoint);
}
progress = Math.max(0, Math.min(1, progress));
if (Math.abs(progress - cache.lastProgress) < 0.005) return;
cache.lastProgress = progress;
if (overlay) overlay.style.opacity = progress.toFixed(3);
if (content) content.style.filter = `blur(${progress * 10}px)`;
if (nextElement && nextElement.style) {
const maskGradient = `linear-gradient(to bottom, transparent 0px, black 80px, black 100%)`;
nextElement.style.maskImage = maskGradient;
nextElement.style.webkitMaskImage = maskGradient;
}
});
};
let rafId = null;
const throttledScroll = () => {
if (rafId) return;
rafId = requestAnimationFrame(() => {
handleScroll();
rafId = null;
});
};
window.addEventListener('scroll', throttledScroll, { passive: true });
window.addEventListener('resize', throttledScroll);
handleScroll();
return () => {
window.removeEventListener('scroll', throttledScroll);
window.removeEventListener('resize', throttledScroll);
if (rafId) cancelAnimationFrame(rafId);
};
}, [stackedIndices.join(','), blurIndices.join(',')]);
useEffect(() => {
const updateMargins = () => {
overlayIndices.forEach(index => {
const el = refs.current[index];
if (el) {
el.style.marginBottom = `-${el.offsetHeight}px`;
}
});
};
updateMargins();
window.addEventListener('load', updateMargins);
return () => window.removeEventListener('load', updateMargins);
}, [overlayIndices, children]);
let forceWhite = false;
let skipNextForceWhite = false;
return (
<div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
{Children.map(children, (child, index) => {
if (!child) return null;
const isSticky = stickyIndices.includes(index);
const isStacked = stackedIndices.includes(index);
const isBlurred = blurIndices.includes(index);
const isOverlay = overlayIndices.includes(index);
const isTransparentNext = transparentNextIndices.includes(index);
const topOffset = offsets[index] || 0;
let zIndex = 10 + index;
if (isSticky && !isStacked) {
zIndex = 1000; // Navigation stays on top
} else if (isStacked) {
zIndex = 1 + index; // Stacked stays behind its followers
}
let backgroundColor = 'transparent';
if (forceWhite && !isStacked) {
if (skipNextForceWhite) {
skipNextForceWhite = false;
} else {
backgroundColor = 'var(--background-neutral--default, #ffffff)';
}
}
if (isStacked) {
forceWhite = true;
skipNextForceWhite = isTransparentNext;
}
return (
<div
ref={el => refs.current[index] = el}
key={index}
style={{
position: isSticky ? 'sticky' : 'relative',
top: isSticky ? topOffset : undefined,
zIndex: zIndex,
display: 'flex',
flexDirection: 'column',
backgroundColor: backgroundColor
}}
>
{isStacked ? (
<>
<div
className="stacked-white-overlay"
style={{
position: 'absolute',
inset: 0,
backgroundColor: 'var(--background-neutral--default, #ffffff)',
zIndex: 2,
pointerEvents: 'none',
opacity: 0,
}}
/>
<div
className="stacked-content-blur"
style={{
position: 'relative',
zIndex: 1,
width: '100%',
height: '100%',
}}
>
{child}
</div>
</>
) : (
child
)}
</div>
);
})}
</div>
);
}